import { Search, SlidersHorizontal } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import type { RuntimeConfigRow } from '../api';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import { formatArtifactSummary } from '../utils/formatters';
import {
  MANAGED_RUNTIME_CONFIG_VALUE_KEYS,
  toRuntimeConfigValueDrafts,
} from '../utils/runtimeConfigValueValidation';

type RuntimeConfigPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function countAdditionalValues(values: RuntimeConfigRow['values']) {
  return (values || []).filter(
    (value) => !MANAGED_RUNTIME_CONFIG_VALUE_KEYS.has(value.key)
  ).length;
}

// Runtime rules are operational records, so they use the same comparable
// registry language as bundles, release targets, and deployment history.
function RuntimeConfigPage({ openDialog }: RuntimeConfigPageProps) {
  const { session } = useSession();
  const canManage = session?.user?.role !== 'viewer';
  const {
    workspace,
    activeOrganization,
    activeProject,
    activeEnvironment,
    activeReleaseAppVersion,
    channels,
    visibleEnvironments,
    isEnvironmentVisible,
    releaseAppVersionById,
    releaseAppById,
    artifactById,
  } = useWorkspace();

  const { runtimeConfigs, releaseAppVersions } = workspace;
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>(
    'all'
  );

  const visibleRuntimeConfigs = runtimeConfigs.filter((config) =>
    isEnvironmentVisible(config.environment)
  );
  const activeConfigs = visibleRuntimeConfigs.filter((config) => config.isActive);
  const configuredVersionIds = new Set(
    activeConfigs
      .map((config) => config.releaseAppVersionId)
      .filter((id): id is string => Boolean(id))
  );
  const versionsMissingConfig = releaseAppVersions.filter(
    (version) => !configuredVersionIds.has(version.releaseAppVersionId)
  ).length;

  const rows = useMemo(
    () =>
      visibleRuntimeConfigs.map((config) => {
        const version = config.releaseAppVersionId
          ? releaseAppVersionById.get(config.releaseAppVersionId)
          : undefined;
        const app = version
          ? releaseAppById.get(version.releaseAppId)
          : undefined;
        const target = version
          ? channels.find(
              (channel) =>
                channel.releaseAppVersionId === version.releaseAppVersionId &&
                channel.environment === config.environment
            )
          : undefined;
        const artifact = target?.releaseArtifactId
          ? artifactById.get(target.releaseArtifactId)
          : undefined;

        return { config, version, app, target, artifact };
      }),
    [
      artifactById,
      channels,
      releaseAppById,
      releaseAppVersionById,
      visibleRuntimeConfigs,
    ]
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return rows.filter(({ config, version, app, artifact }) => {
      if (statusFilter === 'active' && !config.isActive) return false;
      if (statusFilter === 'disabled' && config.isActive) return false;
      if (!normalizedQuery) return true;
      return [
        config.name,
        config.environment,
        config.platform,
        config.hostname,
        config.nativeAppId,
        config.expressApiHostname,
        version?.version,
        app?.name,
        artifact?.version,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery));
    });
  }, [query, rows, statusFilter]);

  function handleCreateRuntimeConfig() {
    if (!activeOrganization || !activeProject) return;
    const defaultEnvironment: 'dev' | 'staging' | 'production' | 'internal' =
      activeEnvironment &&
      ['dev', 'staging', 'production', 'internal'].includes(
        activeEnvironment.slug
      )
        ? (activeEnvironment.slug as typeof defaultEnvironment)
        : (visibleEnvironments[0]?.slug as typeof defaultEnvironment) || 'dev';
    const defaultReleaseAppVersion = activeReleaseAppVersion || null;

    openDialog({
      kind: 'runtime-config',
      name: `${activeProject.name} ${
        defaultReleaseAppVersion?.version || defaultEnvironment
      } runtime`,
      hostname: '',
      releaseAppVersionId:
        defaultReleaseAppVersion?.releaseAppVersionId || '',
      nativeAppId: '',
      platform: 'web',
      nativeVersionRange: '',
      environment: defaultEnvironment,
      expressApiHostname: '',
      staticCdnPrefix: '',
      analyticsEnabled: false,
      analyticsPageviewsEnabled: false,
      posthogApiKey: '',
      posthogHost: '',
      otelEnabled: false,
      otelCollectorUrl: '',
      priority: '100',
      isActive: true,
      values: [],
    });
  }

  function handleEditRuntimeConfig(config: RuntimeConfigRow) {
    const resolvedReleaseAppVersion =
      releaseAppVersionById.get(config.releaseAppVersionId || '') ||
      activeReleaseAppVersion ||
      null;
    openDialog({
      kind: 'runtime-config',
      runtimeConfigId: config.runtimeConfigId,
      name: config.name,
      hostname: config.hostname || '',
      releaseAppVersionId:
        config.releaseAppVersionId ||
        resolvedReleaseAppVersion?.releaseAppVersionId ||
        '',
      nativeAppId: config.nativeAppId || '',
      platform:
        config.platform === 'ios' || config.platform === 'android'
          ? config.platform
          : 'web',
      nativeVersionRange: config.nativeVersionRange || '',
      environment: config.environment,
      expressApiHostname: config.expressApiHostname,
      staticCdnPrefix: config.staticCdnPrefix || '',
      analyticsEnabled: config.analyticsEnabled,
      analyticsPageviewsEnabled: config.analyticsPageviewsEnabled,
      posthogApiKey: config.posthogApiKey || '',
      posthogHost: config.posthogHost || '',
      otelEnabled: config.otelEnabled,
      otelCollectorUrl: config.otelCollectorUrl || '',
      priority: `${config.priority}`,
      isActive: config.isActive,
      values: toRuntimeConfigValueDrafts(config.values),
    });
  }

  return (
    <SectionCard
      className="runtimeRulesPanel"
      label="Client resolution"
      title="Runtime rules"
      description={`${activeConfigs.length} active · ${
        visibleRuntimeConfigs.length - activeConfigs.length
      } disabled · ${versionsMissingConfig} app version${
        versionsMissingConfig === 1 ? '' : 's'
      } without an active rule`}
      action={
        visibleRuntimeConfigs.length > 0 ? (
          <div className="bundleToolbar runtimeRulesToolbar">
            <label className="bundleSearch">
              <Search size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search app, environment, or endpoint"
                aria-label="Search runtime rules"
              />
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value as 'all' | 'active' | 'disabled'
                )
              }
              aria-label="Filter runtime rules by status"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        ) : undefined
      }
    >
      {visibleRuntimeConfigs.length === 0 ? (
        <EmptyState
          icon={<SlidersHorizontal size={20} />}
          title={
            runtimeConfigs.length > 0
              ? 'No visible runtime rules'
              : 'No runtime rules yet'
          }
          description={
            runtimeConfigs.length > 0
              ? 'Show an environment to include its retained runtime rules.'
              : 'Add a rule to tell clients which API, assets, and update target to use.'
          }
          action={
            runtimeConfigs.length === 0 && canManage ? (
              <button
                type="button"
                className="secondaryButton"
                onClick={handleCreateRuntimeConfig}
              >
                Create runtime rule
              </button>
            ) : undefined
          }
        />
      ) : filteredRows.length === 0 ? (
        <EmptyState
          compact
          icon={<Search size={18} />}
          title="No matching runtime rules"
          description="Try another app, environment, endpoint, or status."
          action={
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setQuery('');
                setStatusFilter('all');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div
          className="releaseTableWrap runtimeRulesTableWrap"
          role="region"
          aria-label="Runtime rules table"
          tabIndex={0}
        >
          <table className="releaseTable runtimeRulesTable">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Matches</th>
                <th>Delivers</th>
                <th>Release target</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(({ config, version, app, target, artifact }) => (
                <tr key={config.runtimeConfigId}>
                  <td data-label="Rule">
                    <div className="releaseIdentity">
                      <div className="runtimeRuleName">
                        <strong>{config.name}</strong>
                        <StatusChip tone={config.isActive ? 'success' : 'neutral'}>
                          {config.isActive ? 'Active' : 'Disabled'}
                        </StatusChip>
                      </div>
                      <span>Priority {config.priority}</span>
                    </div>
                  </td>
                  <td data-label="Matches">
                    <div className="releaseIdentity">
                      <strong>
                        {app?.name ||
                          (config.platform === 'web'
                            ? config.hostname || 'All web clients'
                            : 'App not recorded')}
                      </strong>
                      <span>{config.platform?.toUpperCase() || 'WEB'} · {config.environment}
                        {version ? ` · ${version.version}` : ''}</span>
                    </div>
                  </td>
                  <td data-label="Delivers">
                    <div className="releaseIdentity">
                      <strong>{config.expressApiHostname || 'Not configured'}</strong>
                      <span>
                        {countAdditionalValues(config.values)} additional {countAdditionalValues(config.values) === 1 ? 'value' : 'values'}
                        {config.analyticsEnabled ? ' · analytics' : ''}
                        {config.otelEnabled ? ' · telemetry' : ''}
                      </span>
                    </div>
                  </td>
                  <td data-label="Release target">
                    <div className="releaseIdentity">
                      <strong>
                        {target
                          ? artifact
                            ? formatArtifactSummary(artifact)
                            : 'No bundle deployed'
                          : config.platform === 'web'
                            ? 'Web runtime only'
                            : 'No exact target'}
                      </strong>
                      <span>
                        {target
                          ? `${app?.name || 'App'} · ${target.environment}`
                          : config.platform === 'web'
                            ? 'OTA does not apply'
                            : 'Check app version and environment'}
                      </span>
                    </div>
                  </td>
                  <td className="releaseTableAction" data-label="Action">
                    <button
                      type="button"
                      className="secondaryButton"
                      onClick={() => handleEditRuntimeConfig(config)}
                      disabled={!canManage}
                      title={canManage ? undefined : 'Viewers cannot edit runtime rules'}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export { RuntimeConfigPage };
